import { nextTick } from 'process';

import { memo, useCallback, useState, useEffect, useRef } from 'react';

import { SearchInitInfo } from '@wconcept/wconcept-ui-renewal/@types/search';
import Input from '@wconcept/wconcept-ui-renewal/components/atoms/Input';
import SchAutoListDesktop from '@wconcept/wconcept-ui-renewal/components/molecules/_desktop/SchAutoListDesktop';
import SchKeywordListDesktop from '@wconcept/wconcept-ui-renewal/components/molecules/_desktop/SchKeywordListDesktop';
import { classNameBind } from '@wconcept/wconcept-common/utils/commonUtils';
import {
  SearchKeywordInfo,
  PopularKeywordInfo,
} from '@wconcept/wconcept-common/models/renewal/search';
import {
  Brand,
  SearchAutoCompleteContent,
} from '@wconcept/wconcept-common/models/display/search';
import { SearchBar } from '@wconcept/wconcept-common/models/renewal/gnb';
import * as S from './style';

interface IProps {
  /** className */
  className?: string;
  /** isNativeAppBoth */
  isNativeAppBoth: boolean;
  /** keyword */
  keyword?: string;
  /** isSearchOpen */
  isSearchOpen?: boolean;
  /** recentSearchKeywordsList */
  recentSearchKeywordsList: string;
  /** isDesktop */
  isDesktop: boolean;
  /** searchPopularWords */
  searchPopularWords: PopularKeywordInfo;
  /** searchAutoKeywordInfo */
  searchAutoKeywordInfo: SearchAutoCompleteContent;
  /** searchInitInfo */
  searchInitInfo: SearchKeywordInfo;
  /** searchClose */
  searchClose?: () => void;
  /** onSearchClick */
  onSearchClick?: (
    e: HTMLInputElement,
    recentKeywordsList: string[],
    searchInitInfo?: SearchKeywordInfo
  ) => void;
  /** onPressEnter */
  onPressEnter?: (value: string, recentKeywords: string[]) => void;
  /** onClickAutoSearchResult */
  onClickAutoSearchResult: (
    value: string,
    type: string,
    recentKeywordResult: string[],
    item?: Brand,
    no?: number
  ) => void;
  /** onKeywordClick */
  onKeywordClick: (
    param: string,
    type: string,
    recentKeywords: string[],
    no: number
  ) => void;
  /** requestAutoComplete */
  requestAutoComplete?: (keyword: string) => void;
  /** requestSearchPopularWords */
  requestSearchPopularWords?: () => void;
}

const SchAreaDesktop = ({
  keyword = '',
  isNativeAppBoth,
  isSearchOpen = false,
  className,
  recentSearchKeywordsList,
  isDesktop,
  searchPopularWords,
  searchAutoKeywordInfo,
  searchInitInfo,
  searchClose,
  onSearchClick,
  onPressEnter,
  onClickAutoSearchResult,
  onKeywordClick,
  requestAutoComplete,
  requestSearchPopularWords,
}: IProps) => {
  const [$keyword, setKeyword] = useState({ value: keyword });
  const [$isSearchOpen, setIsSearchOpen] = useState(isSearchOpen);
  const [searchResults, setSearchResults] = useState<SearchAutoCompleteContent>(
    {
      total: 0,
      keywordList: [],
      brandList: [],
    }
  );
  const [$isActiveClassName, setIsActiveClassName] = useState(false);
  const [$isFocus, setIsFocus] = useState(false);
  const [isKeywordList, setIsKeywordList] = useState<boolean>(false);
  const showAreaRef = useRef<HTMLDivElement>(null); // 검색창 클릭되면 보여주는 팝업의 ref
  const [recentKeywordsList, setRecentKeywordsList] = useState<string[]>([]); // 최근 검색어

  useEffect(() => {
    const result =
      $isSearchOpen ||
      searchResults?.brandList?.length > 0 ||
      searchResults?.keywordList?.length > 0;
    setIsActiveClassName(result);
  }, [$isSearchOpen, searchResults]);

  useEffect(() => {
    const getSearchPopularWords = () => {
      requestSearchPopularWords?.();
    };
    if ($isSearchOpen) {
      getSearchPopularWords();
    }
  }, [$isSearchOpen]);

  useEffect(() => {
    setIsSearchOpen(isSearchOpen);
  }, [isSearchOpen]);

  useEffect(() => {
    setKeyword({ value: keyword });
  }, [keyword]);

  const resetKeyowrd = useCallback(() => {
    setSearchResults({
      total: 0,
      keywordList: [],
      brandList: [],
    });
  }, []);

  useEffect(() => {
    const getAutoComplete = async () => {
      // request.current = axios.CancelToken.source();
      requestAutoComplete?.($keyword.value);
      if (searchAutoKeywordInfo) {
        setSearchResults(searchAutoKeywordInfo);
        setIsKeywordList(searchAutoKeywordInfo.total === 0);
      }
    };
    if ($keyword.value !== '' && $keyword.value && $keyword.value.trim()) {
      getAutoComplete();
    } else {
      // request.current?.cancel();
    }
  }, [$keyword, requestAutoComplete, searchAutoKeywordInfo]);

  const onClose = useCallback(() => {
    setIsSearchOpen(false);
    setIsActiveClassName(false);
    setIsKeywordList(false);
    setIsFocus(false);
    if (searchClose) {
      searchClose();
    }
  }, [searchClose]);

  // EVENT HANDLER : 검색창 외부 클릭시 이벤트
  const clickOutside = useCallback(
    (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (showAreaRef.current && !showAreaRef.current.contains(target)) {
        onClose();
      }
    },
    [onClose]
  );

  // mousedown 관련
  useEffect(() => {
    if (recentSearchKeywordsList) {
      setRecentKeywordsList(recentSearchKeywordsList.split(','));
    }
    document.addEventListener('mousedown', clickOutside, true);
    return () => {
      document.removeEventListener('mousedown', clickOutside, true);
    };
  }, [clickOutside, recentSearchKeywordsList]);

  //= ================== ITEM HANDLER ======================
  // ITEM HANDLER : onInputKeyword
  const onInputKeyword = useCallback(
    (value: string) => {
      setKeyword({ value });
      if (value === '') {
        setIsKeywordList(true);
        resetKeyowrd();
      } else {
        setIsKeywordList(false);
      }
    },
    [resetKeyowrd]
  );

  // ITEM HANDLER : onChangeKeyword
  const onChangeKeyword = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      onInputKeyword(v);
    },
    [onInputKeyword]
  );

  // ITEM HANDLER : onFocusKeyword
  const onFocusKeyword = useCallback(
    (e: React.FocusEvent<HTMLInputElement>) => {
      const v = e.target.value;
      setIsFocus(true);
      setIsSearchOpen(true);
      onInputKeyword(v);
    },
    [onInputKeyword]
  );

  // ITEM HANDLER : onPressEnterKeyword
  const onPressEnterKeyword = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      const target = e.target as HTMLInputElement;
      if (target.value === '') {
        window.alert('검색어를 입력하세요.');
      } else {
        onPressEnter?.(target.value, recentKeywordsList);
      }
      onClose();
    },
    [onClose, onPressEnter, recentKeywordsList]
  );

  const onSearch = useCallback(
    (e: HTMLInputElement) => {
      onSearchClick?.(e, recentKeywordsList, searchInitInfo);
      onClose();
    },
    [onClose, onSearchClick, recentKeywordsList, searchInitInfo]
  );

  const renderSearchResult = useCallback(() => {
    if ($isFocus) {
      if (searchResults && searchResults?.total > 0) {
        return (
          <SchAutoListDesktop
            isDesktop
            isNativeAppBoth={isNativeAppBoth}
            keyword={$keyword.value}
            searchResults={searchResults}
            onClick={(
              targetKeyword: string,
              url: string,
              type: string,
              item?: Brand,
              no?: number
            ) => {
              setKeyword({ value: targetKeyword });
              onClickAutoSearchResult?.(
                url,
                type,
                recentKeywordsList,
                item,
                no
              );
              setIsFocus(false);
            }}
            onClose={() => {
              setIsFocus(false);
            }}
          />
        );
      }
      return (
        <>
          <SchKeywordListDesktop
            isNativeAppBoth={isNativeAppBoth}
            recentSrch={recentKeywordsList.slice(0, 10)}
            popularSrch={searchPopularWords?.popularWordsInfo}
            onClickDel={value => {
              setRecentKeywordsList(value);
            }}
            onClick={(value: string, type: string, no: number) => {
              setKeyword({ value });
              onKeywordClick?.(value, type, recentKeywordsList, no);
              setIsFocus(false);
            }}
            onClose={() => {
              setIsKeywordList(false);
              setIsFocus(false);
            }}
          />
        </>
      );
    }
    return null;
  }, [
    $isFocus,
    searchResults,
    isNativeAppBoth,
    recentKeywordsList,
    searchPopularWords?.popularWordsInfo,
    $keyword.value,
    onClickAutoSearchResult,
    onKeywordClick,
  ]);

  let placeholder = '';
  if (searchInitInfo?.searchTitle && $isFocus) {
    placeholder = '';
  }
  if (searchInitInfo?.searchTitle) {
    placeholder = searchInitInfo?.searchTitle || '';
  }

  return (
    <S.SchAreaDesktop
      className={classNameBind([
        'area-search',
        $isActiveClassName || $isFocus ? 'active' : '',
        className || '',
      ])}
      ref={showAreaRef}
    >
      <div className="inner-sch">
        <Input
          id="searchInpHeader"
          type="search"
          value={$keyword.value}
          placeholder={placeholder}
          maxLength={80}
          autoComplete="off"
          searchBtn
          onChange={onChangeKeyword}
          onPressEnter={onPressEnterKeyword}
          onFocus={onFocusKeyword}
          onSearch={onSearch}
        />
        {renderSearchResult()}
      </div>
    </S.SchAreaDesktop>
  );
};

export default memo(SchAreaDesktop);
